// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React from 'react';
import { Switch, FormLabel, makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { PropTypes } from 'prop-types';

import {
  MaskedFormTextField,
  RadioGroupSection,
  FormAutoCompleteCombo,
  FormTextField,
} from '../../components';
import { getI18n } from '../../utils/intl';
import styles from './styles';

export default function DynamicForm(props) {
  const { fields, handleChange, index } = props;
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const intl = getI18n(useIntl());

  const renderFormTextField = (field) => {
    const TextField = field.secure ? MaskedFormTextField : FormTextField;
    const requiredDataMissing = field.required && !(field.value && field.value.trim().length);

    return <TextField
      error={requiredDataMissing}
      errorHelperText={requiredDataMissing}
      id={field.name}
      name={field.label}
      handleChange={(value) => handleChange(value, field.name, index)}
      helperText={requiredDataMissing && intl.getI18nMessage('label.required.field')}
      value={field.value}
      disabled={field.disabled}
      optional={!field.required && !field.disabled}
    />
  }

  return (
    <div data-apim-test="dynamic-form-container">
      {fields && fields.map((field) =>
        <div key={field.name}>
          {field.type === 'boolean' &&
            <div data-apim-test={field.name} className={classes.fieldContainer}>
              <FormLabel
                className={classes.formLabel}
              >
                {field.label}
              </FormLabel>
              <br/>
              <div>
                No
                <Switch
                  checked={field.value === 'true'}
                  onChange={(e) => handleChange(e.target.checked, field.name, index)}
                />
                Yes
              </div>
            </div>
          }
          {(field.type === 'string' || field.type === 'int' || field.type === 'integer') && renderFormTextField(field)}
          {field.type === 'radio' &&
            <RadioGroupSection
              id={field.name}
              name={field.label}
              onChange={(e) => handleChange(e.target.value, field.name, index)}
              selected={field.value}
              data={field.data}
            />
          }
          {field.type === 'autocomplete' &&
            <FormAutoCompleteCombo
              data={field.data}
              selectedValues={field.value}
              onChange={(e, value) => handleChange(value, field.name, index)}
              loading={field.isLoad}
              isSecondaryText={field.isSecondaryText}
              id={field.name}
              inputLabel={field.inputLabel}
              hideLabel={field.hideLabel}
              optional={field.optional}
              name={field.label}
              hideHelper={field.hideLabel}
              helperText={field.helperText}
              required={field.required}
            />
          }
          {
            // The field.values is array of values instead of objects
            // expected by FormAutoCompleteCombo when field.type === 'select'.
            // Using a additional <FormAutoCompleteCombo/> to work with.
            field.type === 'select' &&
            <FormAutoCompleteCombo
            data={field.values}
            value={field.value ?? ""}
            getOptionLabel={(option) => option}
            getOptionSelected = {(option, value) => value ? option === value : false}
            renderOption={(option) => (<div>{option}</div>)}
            selectedValues={field.value}
            onChange={(e, value) => handleChange(value ?? "", field.name, index)}
            id={field.name}
            name={field.label}
            filterSelectedOptions={false}
            error={field.required && !(field.value && field.value.trim().length)}
            optional={!field.required}
            textFieldHelperText={
              field.required
              && !(field.value && field.value.trim().length)
              && intl.getI18nMessage('label.required.field')}
          />
          }
        </div>,
      )}
    </div>
  );
}

DynamicForm.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  handleChange: PropTypes.func,
  index: PropTypes.number,
};
